import { ReportingReconciliationPanel } from "./ReportingReconciliationPanel";
import { alpha, useTheme } from "@mui/material/styles";
import { OrganizationChart } from "./OrganizationChart";
import {
  ChevronRight,
  ExpandMore,
  AccountTreeOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  IconButton,
  Stack,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useAuth } from "@features/auth/context/AuthContext";
import { hasPermission } from "@web/config/permissions";
import { apiClient } from "@lib/api/apiClient";
import type { ApiResponse } from "@lib/api/api-types";
import { profileImageUrl } from "@web/utils/upload-url";
export interface Person {
  id: number;
  name: string;
  profileImageUrl?: string;
  departmentId: number | null;
  designationId: number | null;
  department?: string;
  designation?: string;
  levelId: number | null;
  managerId: number | null;
  assignmentId: number | null;
  status: string;
}
interface Option {
  id: number;
  name: string;
  levelId?: number;
}
interface Structure {
  people: Person[];
  departments: Option[];
  designations: Option[];
  levels: Option[];
}
export function OrganizationTree({
  people,
  onEdit,
}: {
  people: Person[];
  onEdit?: (person: Person) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const byId = new Map(people.map((person) => [person.id, person]));
  const reports = new Map<number, Person[]>();
  for (const person of people)
    if (person.managerId && byId.has(person.managerId)) {
      reports.set(person.managerId, [
        ...(reports.get(person.managerId) || []),
        person,
      ]);
    }
  const sort = (list: Person[]) =>
    [...list].sort(
      (a, b) =>
        (a.levelId ?? 99) - (b.levelId ?? 99) || a.name.localeCompare(b.name),
    );
  const roots = sort(
    people.filter((person) => !person.managerId || !byId.has(person.managerId)),
  );
  const hierarchy = roots.filter(
    (person) => person.levelId === 1 || reports.has(person.id),
  );
  const unassigned = roots.filter((person) => !hierarchy.includes(person));
  function toggle(id: number) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function node(person: Person, ancestors: number[] = []) {
    if (ancestors.includes(person.id))
      return (
        <Alert key={person.id} severity="error">
          Invalid reporting cycle
        </Alert>
      );
    const children = sort(reports.get(person.id) || []);
    const open = !collapsed.has(person.id);
    return (
      <Box
        component="li"
        key={person.id}
        sx={{ listStyle: "none", position: "relative", minWidth: 0 }}
      >
        <Card
          variant="outlined"
          sx={{
            p: { xs: 1, sm: 1.5 },
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 1.5 },
            minWidth: 0,
          }}
        >
          {children.length ? (
            <IconButton
              size="small"
              aria-label={`${open ? "Collapse" : "Expand"} ${person.name}'s team`}
              aria-expanded={open}
              aria-controls={`org-reports-${person.id}`}
              onClick={() => toggle(person.id)}
            >
              {open ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, flexShrink: 0 }} />
          )}
          <Avatar
            src={profileImageUrl(person.profileImageUrl)}
            alt={person.name}
            sx={{
              width: { xs: 32, sm: 44 },
              height: { xs: 32, sm: 44 },
              flexShrink: 0,
            }}
          >
            {person.name
              .trim()
              .split(/\s+/)
              .map((n) => n[0])
              .slice(0, 2)
              .join("")}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
            <Typography fontWeight={600}>{person.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {person.designation || "Designation not set"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {person.department || "Department not set"} ·{" "}
              {person.levelId ? `Level ${person.levelId}` : "Level not set"}
              {children.length
                ? ` · ${children.length} direct report${children.length === 1 ? "" : "s"}`
                : ""}
            </Typography>
          </Box>
          {onEdit && (
            <Button
              size="small"
              aria-label="Edit reporting"
              onClick={() => onEdit(person)}
              sx={{ flexShrink: 0, minWidth: 0 }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Edit reporting
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "inline", sm: "none" } }}
              >
                Edit
              </Box>
            </Button>
          )}
        </Card>
        {children.length > 0 && open && (
          <Box
            component="ul"
            id={`org-reports-${person.id}`}
            aria-label={`${person.name}'s direct reports`}
            sx={{
              m: 0,
              ml: { xs: 1, sm: 2.5 },
              pl: { xs: 1, sm: 2.5 },
              borderLeft: "1px solid",
              borderColor: "divider",
              "& > li::before": {
                content: '""',
                position: "absolute",
                left: { xs: -8, sm: -20 },
                top: 32,
                width: { xs: 8, sm: 20 },
                borderTop: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            {children.map((child) => node(child, [...ancestors, person.id]))}
          </Box>
        )}
      </Box>
    );
  }
  return (
    <Stack spacing={2} aria-label="Organizational structure">
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        <AccountTreeOutlined color="primary" />
        <Typography fontWeight={600} sx={{ flex: 1 }}>
          Organization · {people.length} people
        </Typography>
        <Button size="small" onClick={() => setCollapsed(new Set())}>
          Expand all
        </Button>
        <Button
          size="small"
          onClick={() => setCollapsed(new Set(reports.keys()))}
        >
          Collapse all
        </Button>
      </Stack>
      {people.length === 0 && (
        <Alert severity="info">No staff available.</Alert>
      )}
      {people.length > 0 && roots.length === 0 && (
        <Alert severity="error">
          Reporting relationships need review: no root could be found.
        </Alert>
      )}
      {hierarchy.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Reporting hierarchy
          </Typography>
          <Box
            component="ul"
            aria-label="Reporting hierarchy"
            sx={{ m: 0, p: 0 }}
          >
            {hierarchy.map((person) => node(person))}
          </Box>
        </Box>
      )}
      {unassigned.length > 0 && (
        <Box>
          <Typography variant="h6">
            No reporting manager ({unassigned.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These people are not connected to a manager.{" "}
            {onEdit
              ? "Use Edit reporting to place them in the hierarchy."
              : "Set their reporting manager in Organization Structure to connect them."}
          </Typography>
          <Box
            component="ul"
            aria-label="People without a reporting manager"
            sx={{ m: 0, p: 0 }}
          >
            {unassigned.map((person) => node(person))}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
export function searchOrganizationPeople(people: Person[], search: string) {
  const term = search.trim().toLowerCase();
  if (!term) return people;
  const byId = new Map(people.map((p) => [p.id, p]));
  const selected = new Set<number>();
  for (const person of people)
    if (
      `${person.name} ${person.department || ""} ${person.designation || ""}`
        .toLowerCase()
        .includes(term)
    ) {
      let current: Person | undefined = person;
      while (current && !selected.has(current.id)) {
        selected.add(current.id);
        current = current.managerId ? byId.get(current.managerId) : undefined;
      }
    }
  return people.filter((p) => selected.has(p.id));
}
export function OrganizationPage() {
  const { user } = useAuth();
  return user ? (
    <OrganizationEditor
      canEdit={hasPermission(user.role, "hierarchy:manage", user.permissions)}
    />
  ) : (
    <Alert severity="error">Sign in to view the organization.</Alert>
  );
}
function OrganizationEditor({ canEdit }: { canEdit: boolean }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [tab, setTab] = useState<"structure" | "history" | "reconciliation">(
    "structure",
  );
  const [peopleSearch, setPeopleSearch] = useState("");
  const [view, setView] = useState<"chart" | "tree">("chart");
  const cache = useQueryClient();
  const [editing, setEditing] = useState<Person | null>(null);
  const [departmentId, setDepartment] = useState("");
  const [designationId, setDesignation] = useState("");
  const [managerId, setManager] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{
    affectedUserIds: number[];
    ownershipChanges: number;
  } | null>(null);
  const [allowCrossDepartment, setAllowCrossDepartment] = useState(false);
  const { user } = useAuth();
  const effectiveAccess = useQuery({
    queryKey: ["organization-access", editing?.id],
    enabled: canEdit && Boolean(editing),
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<{ scope: string; userIds: number[] }>>(
          `/organization/people/${editing!.id}/access`,
        )
      ).data.data,
  });
  const [historyPage, setHistoryPage] = useState(1);
  const query = useQuery({
    queryKey: ["organization"],
    queryFn: async () =>
      (await apiClient.get<ApiResponse<Structure>>("/organization")).data.data,
  });
  const history = useQuery({
    queryKey: ["organization-history", historyPage],
    enabled: canEdit && tab === "history",
    queryFn: async () =>
      (
        await apiClient.get<
          ApiResponse<{
            items: {
              id: number;
              user_id: number;
              name: string;
              managerName: string;
              assignedBy: string;
              endedBy: string;
              effective_from: string;
              effective_to: string | null;
              designation: string;
              department: string;
            }[];
            pagination: { total: number };
          }>
        >("/organization/history", { params: { page: historyPage, limit: 25 } })
      ).data.data,
  });
  function edit(person: Person) {
    setEditing(person);
    setDepartment(String(person.departmentId || ""));
    setDesignation(String(person.designationId || ""));
    setManager(String(person.managerId || ""));
    setError("");
    setPreview(null);
    setAllowCrossDepartment(false);
  }
  async function save(previewOnly = false) {
    if (!editing) return;
    setSaving(true);
    setError("");
    try {
      const result = await apiClient.put(`/organization/people/${editing.id}`, {
        departmentId: Number(departmentId),
        designationId: Number(designationId),
        managerId: managerId ? Number(managerId) : null,
        expectedAssignmentId: editing.assignmentId,
        preview: previewOnly,
        allowCrossDepartment,
      });
      if (previewOnly) {
        setPreview(result.data.data);
        return;
      }
      setPreview(null);
      setEditing(null);
      setSuccess("Organizational assignment saved.");
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["organization"] }),
        cache.invalidateQueries({ queryKey: ["organization-history"] }),
        cache.invalidateQueries({ queryKey: ["users"] }),
        cache.invalidateQueries({ queryKey: ["reporting-reconciliation"] }),
        cache.invalidateQueries({ queryKey: ["organization-access"] }),
        cache.invalidateQueries({ queryKey: ["leads"] }),
        cache.invalidateQueries({ queryKey: ["sales-structure"] }),
        cache.invalidateQueries({ queryKey: ["lead-dashboard"] }),
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save structure");
    } finally {
      setSaving(false);
    }
  }
  const level =
    query.data?.designations.find((d) => d.id === Number(designationId))
      ?.levelId ??
    (Number(designationId) === editing?.designationId
      ? editing.levelId
      : undefined);
  return (
    <Stack
      spacing={1.25}
      sx={{
        p: { xs: 1, md: 1.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        background: dark
          ? "linear-gradient(135deg,rgba(4,18,34,.82),rgba(4,18,34,.28))"
          : theme.palette.background.paper,
        minWidth: 0,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        gap={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, fontSize: { xs: 24, md: 28 } }}
          >
            Organization Structure
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Our people, departments and reporting lines.
          </Typography>
        </Box>
        <TextField
          size="small"
          label="Search people, department"
          value={peopleSearch}
          onChange={(e) => setPeopleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { md: 290 } }}
        />
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Tabs
          sx={{
            flex: "1 1 260px",
            minWidth: 0,
            minHeight: 40,
            "& .MuiTab-root": { minHeight: 40, px: 1.5, fontSize: 13 },
          }}
          value={canEdit ? tab : "structure"}
          onChange={(_, value: "structure" | "history" | "reconciliation") =>
            setTab(value)
          }
          aria-label="Organization sections"
        >
          <Tab
            label="Structure"
            value="structure"
            id="organization-tab-structure"
            aria-controls="organization-panel-structure"
          />
          {canEdit && (
            <Tab
              label="Assignment History"
              value="history"
              id="organization-tab-history"
              aria-controls="organization-panel-history"
            />
          )}
          {canEdit && (
            <Tab label="Legacy Reconciliation" value="reconciliation" />
          )}
        </Tabs>
        {(!canEdit || tab === "structure") && (
          <ToggleButtonGroup
            exclusive
            value={view}
            onChange={(_, value: "chart" | "tree" | null) => {
              if (value) setView(value);
            }}
            size="small"
            aria-label="Structure layout"
          >
            <ToggleButton value="chart">Chart</ToggleButton>
            <ToggleButton value="tree">Tree</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button
          size="small"
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          onClick={() =>
            void (canEdit && tab === "history"
              ? history.refetch()
              : query.refetch())
          }
        >
          {canEdit && tab === "history"
            ? "Refresh history"
            : "Refresh structure"}
        </Button>
      </Stack>
      {success && (
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}
      {query.isError && <Alert severity="error">{query.error.message}</Alert>}
      {query.isLoading && (
        <Typography role="status">Loading organization…</Typography>
      )}
      {query.data && (!canEdit || tab === "structure") && (
        <Box
          role="tabpanel"
          id="organization-panel-structure"
          aria-labelledby="organization-tab-structure"
          sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2,1fr)",
                lg: "repeat(5,1fr)",
              },
              gap: 1,
            }}
          >
            {query.data.levels.map((l) => {
              const color = (
                dark
                  ? ["#f4ca70", "#38bdf8", "#c084fc", "#2dd4bf", "#fb923c"]
                  : ["#8a5600", "#036b9e", "#7c3caf", "#066e67", "#a04407"]
              )[(l.id - 1) % 5];
              return (
                <Box
                  key={l.id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    p: 0.75,
                    border: `1px solid ${color}66`,
                    borderLeft: `3px solid ${color}`,
                    borderRadius: 1.5,
                    background: `linear-gradient(100deg,${color}18,transparent)`,
                    boxShadow: `0 0 12px ${color}10`,
                  }}
                >
                  <AccountTreeOutlined sx={{ fontSize: 20, color }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color, fontWeight: 700 }}
                    >
                      Level {l.id}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {l.name}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          {peopleSearch && (
            <Typography variant="caption" color="text.secondary">
              Search results include reporting managers to keep connections
              accurate.
            </Typography>
          )}
          {view === "chart" ? (
            <OrganizationChart
              defaultExpanded={Boolean(peopleSearch)}
              key={peopleSearch}
              people={searchOrganizationPeople(query.data.people, peopleSearch)}
              onEdit={canEdit ? edit : undefined}
            />
          ) : (
            <OrganizationTree
              key={peopleSearch}
              people={searchOrganizationPeople(query.data.people, peopleSearch)}
              onEdit={canEdit ? edit : undefined}
            />
          )}
        </Box>
      )}
      {canEdit && tab === "history" && (
        <Box
          role="tabpanel"
          id="organization-panel-history"
          aria-labelledby="organization-tab-history"
          sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
          {history.isLoading && (
            <Typography role="status">Loading assignment history…</Typography>
          )}
          {history.isError && (
            <Alert severity="error">{history.error.message}</Alert>
          )}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: "divider",
              background: dark
                ? "rgba(5,22,36,.65)"
                : theme.palette.background.paper,
            }}
          >
            <Table size="small" aria-label="Assignment history">
              <TableHead
                sx={{ background: alpha(theme.palette.primary.main, 0.06) }}
              >
                <TableRow>
                  {[
                    "Date & time (Dubai)",
                    "Staff name",
                    "Department / designation",
                    "Reporting manager",
                    "Assigned by",
                    "Status",
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        whiteSpace: "nowrap",
                        fontSize: 11,
                        textTransform: "uppercase",
                        color: "text.secondary",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.data?.items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                      {new Date(row.effective_from).toLocaleString("en-GB", {
                        timeZone: "Asia/Dubai",
                      })}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          src={profileImageUrl(
                            query.data?.people.find((p) => p.id === row.user_id)
                              ?.profileImageUrl,
                          )}
                          alt={row.name}
                          sx={{ width: 28, height: 28, fontSize: 11 }}
                        >
                          {row.name.slice(0, 1)}
                        </Avatar>
                        <Typography variant="body2">{row.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {row.department} · {row.designation}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {row.managerName || "No reporting manager"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {row.assignedBy}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {row.effective_to
                        ? `Closed by ${row.endedBy} · ${new Date(row.effective_to).toLocaleString("en-GB", { timeZone: "Asia/Dubai" })}`
                        : "Current"}
                    </TableCell>
                  </TableRow>
                ))}
                {history.data?.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No assignment history yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={1}>
            <Button
              disabled={historyPage === 1}
              onClick={() => setHistoryPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Typography>Page {historyPage}</Typography>
            <Button
              disabled={
                !history.data ||
                historyPage * 25 >= history.data.pagination.total
              }
              onClick={() => setHistoryPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </Box>
      )}
      {canEdit && tab === "reconciliation" && (
        <ReportingReconciliationPanel
          people={query.data?.people || []}
          onReview={edit}
        />
      )}
      <Dialog
        open={canEdit && !!editing}
        onClose={() => {
          if (!saving) setEditing(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit {editing?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              select
              label="Department"
              value={departmentId}
              onChange={(e) => {
                setDepartment(e.target.value);
                setPreview(null);
              }}
            >
              {query.data?.departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Designation"
              value={designationId}
              onChange={(e) => {
                setDesignation(e.target.value);
                setManager("");
              }}
            >
              {editing?.designationId &&
                !query.data?.designations.some(
                  (d) => d.id === editing.designationId,
                ) && (
                  <MenuItem value={editing.designationId} disabled>
                    {editing.designation} (retained current designation)
                  </MenuItem>
                )}
              {query.data?.designations.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  Level {d.levelId} — {d.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Reporting manager"
              value={managerId}
              onChange={(e) => {
                setManager(e.target.value);
                setPreview(null);
              }}
            >
              <MenuItem value="">No reporting manager</MenuItem>
              {query.data?.people
                .filter(
                  (p) =>
                    p.id !== editing?.id &&
                    p.status === "ACTIVE" &&
                    p.levelId &&
                    level &&
                    p.levelId < level,
                )
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} — {p.designation}
                  </MenuItem>
                ))}
            </TextField>
            {editing && (
              <Button
                color={editing.status === "ACTIVE" ? "warning" : "primary"}
                disabled={saving}
                onClick={() =>
                  void apiClient
                    .patch(`/organization/people/${editing.id}/status`, {
                      status:
                        editing.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                    })
                    .then(async () => {
                      setEditing(null);
                      await cache.invalidateQueries({
                        queryKey: ["organization"],
                      });
                    })
                    .catch(() => setError("Unable to change employee access"))
                }
              >
                {editing.status === "ACTIVE"
                  ? "Deactivate employee access"
                  : "Activate employee access"}
              </Button>
            )}
            {effectiveAccess.data && (
              <Alert severity="info">
                Effective lead scope: {effectiveAccess.data.scope}.{" "}
                {effectiveAccess.data.userIds.length} employees in scope.
              </Alert>
            )}
            {user?.permissions.includes("hierarchy:cross_department") && (
              <Button
                onClick={() => {
                  setAllowCrossDepartment(!allowCrossDepartment);
                  setPreview(null);
                }}
              >
                {allowCrossDepartment
                  ? "Cross-department reporting enabled"
                  : "Allow cross-department reporting"}
              </Button>
            )}
            {preview && (
              <Alert severity="warning">
                Reporting change affects {preview.affectedUserIds.length}{" "}
                employees. Lead ownership changes: {preview.ownershipChanges}.
              </Alert>
            )}
            <Button disabled={saving} onClick={() => void save(true)}>
              Preview access impact
            </Button>
            <Alert severity="info">
              Saving confirms this assignment and closes any previous
              organizational assignment. Existing roles, permissions and lead
              ownership remain unchanged.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || !departmentId || !designationId || !preview}
            onClick={() => void save()}
          >
            Confirm and save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
